import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getSpSetting,
  getSpSettingLoader,
  getSpRosteringSetting,
  getSpRosteringSettingLoader,
} from "store/selector/sp.selector";
import { getUser } from "store/selector/auth.selector";
import {
  extractCareSector,
  getCachedCareSector,
  resolveLoginCareSector,
} from "Utils/utils";

/**
 * Resolves org care sector from Redux settings, auth user, then localStorage cache.
 * isLoading is true only while settings APIs run and no sector is known yet.
 */
const useCareSector = () => {
  const spSetting = useSelector(getSpSetting);
  const spRosteringSetting = useSelector(getSpRosteringSetting);
  const spSettingLoader = useSelector(getSpSettingLoader);
  const spRosteringSettingLoader = useSelector(getSpRosteringSettingLoader);
  const currentUser = useSelector(getUser);

  const careSector = useMemo(() => {
    const fromCache = getCachedCareSector();
    const fromUser = resolveLoginCareSector(currentUser);
    return (
      fromCache ||
      fromUser ||
      extractCareSector(spRosteringSetting) ||
      extractCareSector(spSetting) ||
      null
    );
  }, [
    spSetting?.care_sector,
    spRosteringSetting?.care_sector,
    currentUser?.care_sector,
    currentUser?.service_provider?.care_sector,
    currentUser?.service_provider?.settings?.care_sector,
  ]);

  const isLoading = useMemo(() => {
    if (careSector) return false;
    return Boolean(spSettingLoader || spRosteringSettingLoader);
  }, [careSector, spSettingLoader, spRosteringSettingLoader]);

  /** True only while settings APIs run and no sync care sector source is available. */
  const isCareSectorPending = !careSector && isLoading;

  return { careSector, isLoading, isCareSectorPending };
};

export default useCareSector;
